import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

/**
 * List search box. Typing updates the field only.
 * The search runs when the user presses Enter or clicks the icon.
 */
export default function SearchSubmitInput({
  value = '',
  onSearch,
  placeholder = 'Search',
  inputClassName = '',
  name,
  ariaLabel,
  inputProps = {},
}) {
  const [draft, setDraft] = useState(value || '');
  const committedRef = useRef(value || '');
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    const next = value ?? '';
    if (next !== committedRef.current) {
      committedRef.current = next;
      setDraft(next);
    }
  }, [value]);

  const commit = (raw) => {
    const next = String(raw ?? '').trim();
    setDraft(next);
    if (next === committedRef.current) return;
    committedRef.current = next;
    onSearchRef.current?.(next);
  };

  return (
    <div className="relative w-full min-w-0">
      <input
        type="text"
        name={name}
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit(draft);
          }
        }}
        style={{ paddingRight: draft ? '4.25rem' : '2.5rem' }}
        className={inputClassName}
        {...inputProps}
      />
      {draft ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => commit('')}
          className="absolute right-9 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Search"
        onClick={() => commit(draft)}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
      >
        <Search className="w-4 h-4" />
      </button>
    </div>
  );
}
